import _ from "lodash";
import React, { useState, memo } from "react";
import classes from "./SelectResourceDialogue.scss";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import classNames from "classnames";
import AttachmentListWrapper from "./AttachmentListWrapper";
import { Button, EmptyState, IconButton } from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { ResourcesIllustration } from "@toddle-edu/ds-theme";
import { getOrganizationResourcesFeedQuery } from "OrganizationResources/modules/OrganizationResourcesQuery";
import { getOrganizationResourcesFromCache } from "OrganizationResources/modules/OrganizationResourcesGraphqlHelpers";
import { getUnitFlowUtils } from "UnitFlowV3/modules/utils";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getSelectedCourseId } from "modules/CommonSelectors";
import { getUnitResourcesFeedQuery } from "UnitFlowV3/modules/EntityFlow/Query";
import { getUnitResourcesFeedQuery as learningCourseFlowQueries_getUnitResourcesFeedQuery } from "UnitFlowV3/modules/LearningCourseFlow/Query";
import { getIsLearningCoursesEnabledForUnit } from "UnitPlans/modules/UnitViewModeUtils";

const resourceTypes = {
  value: "UNIT_PLAN_RESOURCES",
  label: "Unit plan resources",
  visibleTo: [],
  types: "PLANNER_RESOURCE",
};

const ModalContent = props => {
  const { t, onClose, organizationResources, testId } = props;

  const [selectedResources, setSelecedResources] = useState([]);

  const toggleSelectResources = selectedResources => {
    setSelecedResources(selectedResources);
  };

  const addSelectedResources = () => {
    const { addSelectedResources, onClose } = props;
    addSelectedResources(selectedResources);
    onClose();
  };

  const activityCountClass = classNames(
    { [classes.activityCount]: selectedResources.length > 0 },
    { [classes.noActivityCount]: selectedResources.length == 0 }
  );

  return (
    <React.Fragment>
      <div className={classes.header}>
        <div className={classes.modalHeading}>{t("common:unit_resources")}</div>

        <IconButton
          testId={`${testId}-selectResourceDialog-close`}
          icon={<CloseOutlined variant="subtle" />}
          _legacy_variant="plain"
          variant="neutral"
          type="plain"
          size="medium"
          onClick={onClose}
        />
      </div>
      {!_.isEmpty(organizationResources) ? (
        <React.Fragment>
          <div className={classes.mainContainer}>
            <AttachmentListWrapper
              organizationResources={organizationResources}
              selectedResources={selectedResources}
              toggleSelectResources={toggleSelectResources}
              testId={`${testId}-selectResourceDialog`}
            />
          </div>
          <div className={classes.footer}>
            <div className={activityCountClass}>
              {t("common:resource_selected_count", {
                postProcess: "interval",
                count: selectedResources.length,
              })}
            </div>
            <div className={classes.rightContainer}>
              <Button
                size="medium"
                _legacy_variant="outlined"
                variant="neutral"
                type="outlined"
                onClick={onClose}
                testId={`${testId}-selectResourceDialog-cancel`}
                className={classes.closeButton}
              >
                {t("common:cancel")}
              </Button>
              <Button
                size="medium"
                disabled={_.isEmpty(selectedResources)}
                testId={`${testId}-selectResourceDialog-add`}
                onClick={addSelectedResources}
              >
                {t("common:add")}
              </Button>
            </div>
          </div>
        </React.Fragment>
      ) : (
        <EmptyState
          illustration={ResourcesIllustration}
          title={t("common:no_with_label", {
            label: t("common:resources"),
          })}
          subtitle={t("classRoom:no_resources_in_unit_yet")}
        />
      )}
    </React.Fragment>
  );
};

const ModalContentWithLoader = withLoader(ModalContent);

const SelectResourceDialogue = memo(props => {
  const { onClose } = props;

  return (
    <UIModal
      isOpen
      modalContent={classes.modalContent}
      onRequestClose={onClose}
    >
      <ModalContentWithLoader {...props} />
    </UIModal>
  );
});

SelectResourceDialogue.displayName = "SelectResourceDialogue";

const mapStateToProps = (state, ownProps) => {
  const unitPlanId = _.get(ownProps, "unitPlanId");
  const {
    isUnitFlowRevampEnabled,
    getUnitResourcesVariablesMemoized,
  } = getUnitFlowUtils();

  const isUnitFlowRevamp = isUnitFlowRevampEnabled();

  const orgResourcesVariables = {
    parentId: unitPlanId,
    parentType: "UNIT_PLAN",
    filters: {
      types: resourceTypes.types,
      visibleTo: resourceTypes.visibleTo,
    },
  };

  const learningCourseId =
    _.get(state, "planner.unitPlanData.learningCourseId", null) ||
    getSelectedLearningCourseId(state);
  const classId = getSelectedCourseId(state);

  const unitResourcesVariables = getUnitResourcesVariablesMemoized({
    parentId: unitPlanId,
    learningCourseId,
    classId,
    first: 1000,
  });

  const isLearningCoursesEnabled = getIsLearningCoursesEnabledForUnit();

  return {
    orgResourcesVariables,
    isLearningCoursesEnabled,
    unitResourcesVariables,
    isUnitFlowRevamp,
  };
};

const getUnitResourcesFeedQueryFunc = (query, isEntityFlow) =>
  graphql(query, {
    name: "getUnitResourcesFeed",
    skip: ({ isUnitFlowRevamp, isLearningCoursesEnabled }) =>
      !isUnitFlowRevamp ||
      (isEntityFlow ? isLearningCoursesEnabled : !isLearningCoursesEnabled),
    options: ({ unitResourcesVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: unitResourcesVariables,
    }),
    props({ getUnitResourcesFeed }) {
      const unitResourcesData = getUnitFlowUtils().getUnitResourcesFeed(
        getUnitResourcesFeed
      );

      const unitResourcesEdges = _.get(unitResourcesData, "edges", []);
      const unitResources = _.map(unitResourcesEdges, "node");

      return {
        organizationResources: unitResources,
        isData: !_.isEmpty(getUnitResourcesFeed),
        isLoading: [1, 2].includes(getUnitResourcesFeed.networkStatus),
      };
    },
  });

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, null),
  graphql(getOrganizationResourcesFeedQuery, {
    name: "getOrganizationResourcesFeed",
    skip: ({ isUnitFlowRevamp }) => isUnitFlowRevamp,
    options: ({ orgResourcesVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: orgResourcesVariables,
    }),
    props({
      getOrganizationResourcesFeed,
      ownProps: { orgResourcesVariables },
    }) {
      const organizationResourcesData = getOrganizationResourcesFromCache(
        orgResourcesVariables
      );
      return {
        organizationResources:
          organizationResourcesData?.organizationResources ?? [],
        isData: !_.isEmpty(organizationResourcesData),
        isLoading: [1, 2].includes(getOrganizationResourcesFeed.networkStatus),
      };
    },
  }),
  getUnitResourcesFeedQueryFunc(getUnitResourcesFeedQuery, true),
  getUnitResourcesFeedQueryFunc(
    learningCourseFlowQueries_getUnitResourcesFeedQuery,
    false
  )
)(SelectResourceDialogue);
